import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";
import { toast } from "@/lib/notify";

/**
 * Forces the user to change their password if must_change_password=true.
 * Renders a non-dismissable dialog above the app.
 */
export default function ForcePasswordChangeDialog() {
  const { user, refresh } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);
  // Sevkiyat personeli / Operatör PIN ile giriş yapar; kendisine hiçbir
  // zaman "şifre güncelle" ekranı çıkmamalı — parolayı görmüyor, bilmiyor,
  // kullanmıyor.  Backend hâlâ `must_change_password: True` ile yazıyor
  // (sebep: admin düzenlerken PIN'sizzken de kayıt geçerli olsun), ama UI
  // kararı burada.
  const open =
    user && user !== false && user.must_change_password === true
    && user.role !== "sevkiyat" && user.role !== "operator";

  useEffect(() => {
    if (!open) {
      setCur(""); setNext(""); setNext2("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== next2) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next,
      });
      toast.success("Şifre güncellendi");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissable */ }}>
      <DialogContent
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="force-password-change-dialog"
      >
        <DialogHeader>
          <DialogTitle className="nf-heading flex items-center gap-2">
            <KeyRound size={18} /> Şifrenizi Güncelleyin
          </DialogTitle>
          <DialogDescription>
            Yönetici tarafından oluşturulan geçici şifreyi kullandığınız için devam etmeden önce yeni bir şifre belirlemeniz gerekiyor.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="nf-label">Mevcut (Geçici) Şifre</Label>
            <Input required type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="fpc-current" />
          </div>
          <div className="space-y-1.5">
            <Label className="nf-label">Yeni Şifre (en az 6 karakter)</Label>
            <Input required type="password" minLength={6} value={next} onChange={(e) => setNext(e.target.value)} data-testid="fpc-next" />
          </div>
          <div className="space-y-1.5">
            <Label className="nf-label">Yeni Şifre (Tekrar)</Label>
            <Input required type="password" minLength={6} value={next2} onChange={(e) => setNext2(e.target.value)} data-testid="fpc-next2" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="fpc-submit">
              {busy ? "Güncelleniyor..." : "Şifreyi Güncelle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
